import { Button, Space, Table } from 'antd';
import React, { useMemo, useState } from 'react';
import AppPagination from '../AppPagination';
import TableTitle from './TableTitle';
import { EllipsisOutlined, LoadingOutlined } from '@ant-design/icons';
import './index.scss';
import AppDropdown from '../AppDropdown';

function AppTable({
  columns,
  dataSource,
  rowKey,
  selectedRowKeys,
  setSelectedRowKeys,
  loading = false,
  hasRowAction = false,
  hasRowSelect = false,
  hasPagination = true,
  hasDeleteAction = false,
  hasImport = false,
  totalItems = dataSource?.length || 0,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  onCreate,
  onExport,
  onImport,
  rowAction,
  onEditAction,
  onDeleteAction,
  onRemoveItems,
  onRowSelectionAction,
  rowSelectionActionLabel = '',
  isRowSelectionActionLoading,
  removeItemsLabel = '',
  tableTitle,
  showSizeChanger,
  disableColumnVisibility = false,
  defaultSize = 'middle',
  exportTooltip = 'Export',
  pagination = false,
  rowSelectionClassName = '',
  ...props
}) {
  const [size, setSize] = useState(defaultSize);
  const handleSizeChange = (newSize) => {
    setSize(newSize);
  };

  const [visibleColumns, setVisibleColumns] = useState(
    disableColumnVisibility ? [] : columns.map((col) => col.key)
  );
  const handleColumnVisibilityChange = (checked) => {
    if (!disableColumnVisibility) {
      setVisibleColumns(checked);
    }
  };
  const filteredColumns = useMemo(() => {
    // If column visibility is disabled, return all columns
    if (disableColumnVisibility) {
      return columns.map((col) => ({
        ...col,
        align: col.align || 'center',
        children: col.children?.map((child) => ({
          ...child,
          align: child.align || 'center',
          children: child.children?.map((child) => ({
            ...child,
            align: child.align || 'center',
          })),
        })),
      }));
    }

    return columns
      .filter((col) => visibleColumns.includes(col.key))
      .map((col) => {
        return {
          ...col,
          align: col.align || 'center',
          children: col.children?.map((child) => ({
            ...child,
            align: child.align || 'center',
            children: child.children?.map((child) => ({
              ...child,
              align: child.align || 'center',
            })),
          })),
        };
      });
  }, [columns, visibleColumns, disableColumnVisibility]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const hasSelected = selectedRowKeys?.length > 0;

  const newColumns = useMemo(() => {
    if (!hasRowAction) return filteredColumns;
    return [
      ...filteredColumns,
      {
        // width: '5%',
        title: 'Action',
        key: 'action',
        align: 'center',
        fixed: 'right',
        render: (_, record) =>
          typeof rowAction === 'function' ? (
            rowAction(record)
          ) : (
            // <span className="flex justify-center gap-2">
            //   <Tooltip title="Edit">
            //     <Button
            //       type="text"
            //       onClick={() => onEditAction(record)}
            //       className="text-center px-2"
            //     >
            //       <EditOutlined />
            //     </Button>
            //   </Tooltip>
            //   {hasDeleteAction && (
            //     <Button color="danger" variant="text" onClick={() => onDeleteAction(record)}>
            //       <ClearOutlined />
            //     </Button>
            //   )}
            // </span>
            <AppDropdown
              options={[
                {
                  key: '1',
                  label: 'Edit',
                  onClick: () => onEditAction(record),
                },
                ...(hasDeleteAction
                  ? [
                      {
                        key: '2',
                        label: 'Delete',
                        onClick: () => onDeleteAction(record),
                      },
                    ]
                  : []),
              ]}
              onChange={(item) => item.onClick()}
              placement="bottomRight"
              overlayStyle={{ minWidth: '150px' }}
            >
              <Button type="text" className="px-2">
                <EllipsisOutlined />
              </Button>
            </AppDropdown>
          ),
      },
    ];
  }, [filteredColumns, hasRowAction, rowAction, onEditAction, hasDeleteAction, onDeleteAction]);

  return (
    <>
      {hasRowSelect && (
        <>
          {hasSelected ? (
            <div
              className={`bg-white p-4 rounded-md border flex items-center justify-between my-4 ${rowSelectionClassName}`}
            >
              <span>{`${selectedRowKeys?.length} of ${dataSource?.length} item(s) selected`}</span>
              {(onRemoveItems || onRowSelectionAction) && (
                <Button
                  variant={onRemoveItems ? 'filled' : 'outlined'}
                  color={onRemoveItems ? 'danger' : onRowSelectionAction ? 'default' : ''}
                  onClick={() => {
                    if (onRemoveItems) {
                      return onRemoveItems(selectedRowKeys);
                    }

                    if (onRowSelectionAction) {
                      return onRowSelectionAction(selectedRowKeys);
                    }

                    return;
                  }}
                  loading={isRowSelectionActionLoading}
                  disabled={isRowSelectionActionLoading}
                >
                  {removeItemsLabel || rowSelectionActionLabel}
                </Button>
              )}
            </div>
          ) : null}
        </>
      )}

      <Space direction="vertical" className="w-full">
        <TableTitle
          tableTitle={tableTitle}
          onCreate={onCreate}
          onRefresh={onRefresh}
          onExport={onExport}
          exportTooltip={exportTooltip}
          hasImport={hasImport}
          isLoading={loading}
          columnKeys={columns.map((col) => {
            return {
              label: col.title,
              value: col.key,
            };
          })}
          visibleColumns={visibleColumns}
          disableColumnVisibility={disableColumnVisibility}
          onColumnChange={handleColumnVisibilityChange}
          rowHeight={size}
          onSizeChange={handleSizeChange}
        />
        <Table
          columns={newColumns}
          dataSource={dataSource}
          rowKey={rowKey}
          rowSelection={hasRowSelect ? rowSelection : null}
          pagination={pagination}
          loading={{
            spinning: loading,
            indicator: <LoadingOutlined spin />,
          }}
          scroll={{ x: 'max-content' }}
          size={size}
          bordered
          // className="shadow-sm"
          {...props}
        />
        {hasPagination && dataSource?.length !== 0 && (
          <AppPagination
            totalItems={totalItems}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            showSizeChanger={showSizeChanger}
          />
        )}
      </Space>
    </>
  );
}

export default AppTable;
